/**
 * TypeScript Type Definitions
 * ===========================
 * Shared types matching the backend API schemas.
 */

// =============================================================================
// API Response Types
// =============================================================================

export interface ApiResponse<T> {
  data: T | null
  error: string | null
}

export interface PaginationMeta {
  page: number
  limit: number
  total: number
  total_pages: number
  has_next: boolean
  has_prev: boolean
}

export interface PaginatedResponse<T> {
  data: T[]
  meta: PaginationMeta
  error: string | null
}

// =============================================================================
// Category Types
// =============================================================================

export interface Category {
  id: number
  name: string
  description: string | null
  parent_id: number | null
  created_at: string
  updated_at: string
}

export interface CategoryTree extends Category {
  children: CategoryTree[]
  product_count: number
}

export interface CategoryCreate {
  name: string
  description?: string | null
  parent_id?: number | null
}

export interface CategoryUpdate {
  name?: string
  description?: string | null
  parent_id?: number | null
}

// =============================================================================
// Location Types
// =============================================================================

export interface Location {
  id: number
  name: string
  description: string | null
  created_at: string
  updated_at: string
}

export interface LocationWithCount extends Location {
  product_count: number
}

export interface LocationCreate {
  name: string
  description?: string | null
}

export interface LocationUpdate {
  name?: string
  description?: string | null
}

// =============================================================================
// Manufacturer Types
// =============================================================================

export interface Manufacturer {
  id: number
  name: string
  logo_url: string | null
  created_at: string
  updated_at: string
}

export interface ManufacturerWithCount extends Manufacturer {
  product_count: number
}

export interface ManufacturerCreate {
  name: string
  logo_url?: string | null
}

export interface ManufacturerUpdate {
  name?: string
  logo_url?: string | null
}

// =============================================================================
// Product Image Types
// =============================================================================

export type ImageSource = 'uploaded' | 'web_search' | 'ai_generated' | 'ai_identification'

export interface ProductImage {
  id: number
  product_id: number
  image_url: string
  is_primary: boolean
  source: ImageSource
  alt_text: string | null
  created_at: string
}

export interface ProductImageCreate {
  image_url: string
  is_primary?: boolean
  source?: ImageSource
  alt_text?: string | null
}

export interface ProductImageUpdate {
  is_primary?: boolean
  alt_text?: string | null
}

// =============================================================================
// Product Types
// =============================================================================

export type ProductStatus = 'in_stock' | 'in_use' | 'on_the_way' | 'faulty'

export const PRODUCT_STATUS_LABELS: Record<ProductStatus, string> = {
  in_stock: 'In Stock',
  in_use: 'In Use',
  on_the_way: 'On the Way',
  faulty: 'Faulty',
}

export interface Product {
  id: number
  product_id: string
  sku: string | null
  barcode: string | null
  service_tag: string | null
  name: string
  description: string | null
  details: string | null
  compatible_products: string | null
  quantity: number
  status: ProductStatus
  cost_price: string | null
  sale_price: string | null
  image_url: string | null
  category_id: number | null
  location_id: number | null
  manufacturer_id: number | null
  created_at: string
  updated_at: string
  category: Category | null
  location: Location | null
  manufacturer: Manufacturer | null
  images: ProductImage[]
}

export interface ProductCreate {
  name: string
  description?: string | null
  details?: string | null
  compatible_products?: string | null
  sku?: string | null
  barcode?: string | null
  service_tag?: string | null
  quantity?: number
  status?: ProductStatus
  cost_price?: string | null
  sale_price?: string | null
  image_url?: string | null
  category_id?: number | null
  location_id?: number | null
  manufacturer_id?: number | null
}

export interface ProductUpdate {
  name?: string
  description?: string | null
  details?: string | null
  compatible_products?: string | null
  sku?: string | null
  barcode?: string | null
  service_tag?: string | null
  quantity?: number
  status?: ProductStatus
  cost_price?: string | null
  sale_price?: string | null
  image_url?: string | null
  category_id?: number | null
  location_id?: number | null
  manufacturer_id?: number | null
}

export type SortField = 'name' | 'created_at' | 'updated_at' | 'quantity' | 'product_id'
export type SortOrder = 'asc' | 'desc'

export interface ProductSearchParams {
  page?: number
  limit?: number
  search?: string
  category_id?: number | null
  include_subcategories?: boolean
  location_id?: number | null
  manufacturer_id?: number | null
  min_quantity?: number
  max_quantity?: number
  sort_by?: SortField
  sort_order?: SortOrder
}

// =============================================================================
// User / Auth Types
// =============================================================================

export interface User {
  id: number
  email: string
  name: string
  given_name: string | null
  family_name: string | null
  profile_picture_url: string | null
  is_active: boolean
  created_at: string
  last_login: string | null
}

export interface AuthStatus {
  authenticated: boolean
  user: {
    id: number
    email: string
    name: string
    profile_picture_url: string | null
  } | null
}

// =============================================================================
// Barcode Lookup Types
// =============================================================================

export interface BarcodeLookupResult {
  found: boolean
  barcode: string
  name: string | null
  description: string | null
  brand: string | null
  brand_logo_url: string | null
  category: string | null
  image_url: string | null
  source: string | null
}

export interface BarcodeLookupResponse {
  data: BarcodeLookupResult
}

// =============================================================================
// Image Search Types
// =============================================================================

export interface ImageSearchResult {
  url: string
  thumbnail_url: string
  title: string
  width: number
  height: number
  source_url: string
}

export interface ImageSearchResponse {
  query: string
  total_results: number
  images: ImageSearchResult[]
  error?: string | null  // Error message if search failed
}

// =============================================================================
// SKU Lookup Types
// =============================================================================

export interface SKULookupProduct {
  name: string | null
  description: string | null
  manufacturer: string | null
  compatible_products: string | null
  confidence: 'low' | 'medium' | 'high'
  source_url: string | null
}

export interface SKULookupImage {
  url: string
  thumbnail: string
  title: string
  width: number | null
  height: number | null
}

export interface SKULookupResult {
  sku: string
  product: SKULookupProduct | null
  images: SKULookupImage[]
  error: string | null
}

export interface SKULookupResponse {
  data: SKULookupResult
}

// =============================================================================
// Image Gallery Types
// =============================================================================

export interface ImageGalleryItem {
  id: number
  product_id: number
  image_url: string
  is_primary: boolean
  source: string
  alt_text: string | null
  created_at: string
  product_product_id: string
  product_name: string
  product_category_id: number | null
}

export interface ImageGalleryParams {
  page?: number
  limit?: number
  category_id?: number | null
  include_subcategories?: boolean
}

// =============================================================================
// Moto Location Types
// =============================================================================

export interface MotoLocation {
  id: number
  name: string
  description: string | null
  created_at: string
  updated_at: string
}

export interface MotoLocationCreate {
  name: string
  description?: string | null
}

export interface MotoLocationUpdate {
  name?: string
  description?: string | null
}

// =============================================================================
// Moto Manufacturer Types
// =============================================================================

export interface MotoManufacturer {
  id: number
  name: string
  logo_url: string | null
  created_at: string
  updated_at: string
}

export interface MotoManufacturerCreate {
  name: string
  logo_url?: string | null
}

export interface MotoManufacturerUpdate {
  name?: string
  logo_url?: string | null
}

// =============================================================================
// Compatibility Search Types
// =============================================================================

export interface CompatibilitySearchRequest {
  name: string
  sku?: string | null
  manufacturer?: string | null
  description?: string | null
}

export interface CompatibilitySearchResult {
  compatible_devices: string[]
  compatibility_notes: string | null
  confidence: 'low' | 'medium' | 'high'
  source_url: string | null
  source_type: 'manufacturer_compatibility_tool' | 'product_page' | 'general_database' | null
  error: string | null
}

export interface CompatibilitySearchResponse {
  data: CompatibilitySearchResult
}

// =============================================================================
// Asset Types (ITAM)
// =============================================================================

export type AssetType =
  | 'server'
  | 'switch'
  | 'access_point'
  | 'firewall'
  | 'router'
  | 'nas'
  | 'ups'
  | 'camera'
  | 'doorbell'
  | 'chime'
  | 'other'

export const ASSET_TYPE_LABELS: Record<AssetType, string> = {
  server: 'Server',
  switch: 'Switch',
  access_point: 'Access Point',
  firewall: 'Firewall',
  router: 'Router',
  nas: 'NAS',
  ups: 'UPS',
  camera: 'Camera',
  doorbell: 'Doorbell',
  chime: 'Chime',
  other: 'Other',
}

export type AssetStatus = 'online' | 'offline' | 'degraded' | 'unknown'

export const ASSET_STATUS_LABELS: Record<AssetStatus, string> = {
  online: 'Online',
  offline: 'Offline',
  degraded: 'Degraded',
  unknown: 'Unknown',
}

export const ASSET_STATUS_COLORS: Record<AssetStatus, string> = {
  online: 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300',
  offline: 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300',
  degraded: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300',
  unknown: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
}

// Asset Location
export interface AssetLocation {
  id: number
  name: string
  description: string | null
  created_at: string
  updated_at: string
}

export interface AssetLocationWithCount extends AssetLocation {
  asset_count: number
}

export interface AssetLocationCreate {
  name: string
  description?: string | null
}

export interface AssetLocationUpdate {
  name?: string
  description?: string | null
}

// Asset Manufacturer
export interface AssetManufacturer {
  id: number
  name: string
  logo_url: string | null
  website_url: string | null
  created_at: string
  updated_at: string
}

export interface AssetManufacturerWithCount extends AssetManufacturer {
  asset_count: number
}

export interface AssetManufacturerCreate {
  name: string
  website_url?: string | null
}

export interface AssetManufacturerUpdate {
  name?: string
  logo_url?: string | null
  website_url?: string | null
}

// Asset Image
export interface AssetImage {
  id: number
  image_url: string
  is_primary: boolean
  source: string | null
  created_at: string
}

// iDRAC telemetry types (Dell servers)
export interface IdracPSU {
  name: string
  status: string | null
  health: string | null
  capacity_watts: number | null
  output_watts: number | null
  input_voltage: number | null
  efficiency_rating: string | null
  manufacturer: string | null
  model: string | null
  serial_number: string | null
}

export interface IdracTemperature {
  name: string
  reading_celsius: number | null
}

export interface IdracFan {
  name: string
  status: string | null
  health: string | null
  reading_rpm: number | null
  reading_percent: number | null
}

// Asset
export interface Asset {
  id: number
  asset_id: string
  name: string
  asset_type: AssetType
  model: string | null
  service_tag: string | null

  // Relationships
  location_id: number | null
  manufacturer_id: number | null
  location: AssetLocation | null
  manufacturer: AssetManufacturer | null
  images: AssetImage[]
  network_interfaces: NetworkInterface[]
  storage_drives: StorageDrive[]
  storage_volumes: StorageVolume[]

  // Specifications
  cpu_model: string | null
  cpu_cores: number | null
  cpu_threads: number | null
  ram_gb: number | null
  storage_type: string | null
  storage_capacity_gb: number | null
  storage_count: number | null
  raid_level: string | null
  network_ports: number | null
  network_speed: string | null
  power_watts: number | null
  power_supply_count: number | null
  power_redundant: boolean | null

  // Network Info
  ip_address: string | null
  mac_address: string | null
  management_ip: string | null
  vlan_id: number | null
  gateway: string | null

  // Management
  management_url: string | null
  firmware_version: string | null
  bios_version: string | null

  // Console settings (for virtual console launcher)
  console_type: ConsoleType | null
  management_username: string | null
  // Note: management_password is never returned from API

  // DSM Update status (Synology NAS only)
  dsm_update_available: boolean | null
  dsm_update_info: string | null

  // Infrastructure source tracking
  infrastructure_source: InfrastructureSource | null

  // Firmware update availability
  firmware_update_available: boolean | null
  firmware_update_version: string | null

  // Status
  status: AssetStatus
  last_seen: string | null
  uptime_seconds: number | null

  // iDRAC telemetry data (Dell servers only)
  idrac_psus: string | null  // JSON array of PSU info
  idrac_power_consumed_watts: number | null
  idrac_temperatures: string | null  // JSON array of temp readings
  idrac_fans: string | null  // JSON array of fan status

  // Real-time utilization metrics
  cpu_usage_percent: number | null
  ram_usage_percent: number | null

  // Lifecycle
  purchase_date: string | null
  warranty_expiry: string | null
  cost_price: string | null
  deployment_date: string | null
  notes: string | null

  // Timestamps
  created_at: string
  updated_at: string
}

export interface AssetCreate {
  name: string
  asset_type?: AssetType
  model?: string | null
  service_tag?: string | null
  location_id?: number | null
  manufacturer_id?: number | null

  // Specifications
  cpu_model?: string | null
  cpu_cores?: number | null
  cpu_threads?: number | null
  ram_gb?: number | null
  storage_type?: string | null
  storage_capacity_gb?: number | null
  storage_count?: number | null
  raid_level?: string | null
  network_ports?: number | null
  network_speed?: string | null
  power_watts?: number | null
  power_supply_count?: number | null
  power_redundant?: boolean | null

  // Network Info
  ip_address?: string | null
  mac_address?: string | null
  management_ip?: string | null
  vlan_id?: number | null
  gateway?: string | null

  // Management
  management_url?: string | null
  firmware_version?: string | null
  bios_version?: string | null

  // Console settings (for virtual console launcher)
  console_type?: ConsoleType | null
  management_username?: string | null
  management_password?: string | null  // Write-only

  // DSM Update status (Synology NAS only)
  dsm_update_available?: boolean | null
  dsm_update_info?: string | null

  // Infrastructure source tracking
  infrastructure_source?: InfrastructureSource | null

  // Firmware update availability
  firmware_update_available?: boolean | null
  firmware_update_version?: string | null

  // Lifecycle
  purchase_date?: string | null
  warranty_expiry?: string | null
  cost_price?: string | null
  deployment_date?: string | null
  notes?: string | null
}

export interface AssetUpdate extends Partial<AssetCreate> {
  status?: AssetStatus
}

export type AssetSortField =
  | 'name'
  | 'created_at'
  | 'updated_at'
  | 'asset_id'
  | 'asset_type'
  | 'status'

export interface AssetSearchParams {
  page?: number
  limit?: number
  search?: string
  asset_type?: AssetType | null
  status?: AssetStatus | null
  location_id?: number | null
  manufacturer_id?: number | null
  infrastructure_source?: InfrastructureSource | null
  sort_by?: AssetSortField
  sort_order?: SortOrder
}

export interface AssetStats {
  total_count: number
  by_type: Record<string, number>
  by_status: Record<string, number>
  by_location: Record<string, number>
}

export interface AssetStatusUpdate {
  status: AssetStatus
  last_seen: string
  uptime_seconds?: number | null
}

// =============================================================================
// Network Interface Types
// =============================================================================

export interface NetworkInterface {
  id: number
  asset_id: number
  interface_name: string | null
  ip_address: string | null
  mac_address: string | null
  speed: string | null // Negotiated speed (e.g., "10GbE")
  max_speed: string | null // Maximum supported speed (e.g., "10GbE")
  link_detected: boolean | null // true=connected, false=disconnected, null=unknown
  vlan_id: number | null
  gateway: string | null
  is_primary: boolean
  is_management: boolean
  created_at: string
  updated_at: string
}

export interface NetworkInterfaceCreate {
  interface_name?: string | null
  ip_address?: string | null
  mac_address?: string | null
  speed?: string | null
  max_speed?: string | null
  link_detected?: boolean | null
  vlan_id?: number | null
  gateway?: string | null
  is_primary?: boolean
  is_management?: boolean
}

export type NetworkInterfaceUpdate = Partial<NetworkInterfaceCreate>

// =============================================================================
// Storage Drive Types
// =============================================================================

export type DriveType = 'ssd' | 'hdd' | 'nvme' | 'sas' | 'other'

export const DRIVE_TYPE_LABELS: Record<DriveType, string> = {
  ssd: 'SSD',
  hdd: 'HDD',
  nvme: 'NVMe',
  sas: 'SAS',
  other: 'Other',
}

export type DriveHealth = 'good' | 'warning' | 'critical' | 'failed' | 'unknown'

export const DRIVE_HEALTH_LABELS: Record<DriveHealth, string> = {
  good: 'Good',
  warning: 'Warning',
  critical: 'Critical',
  failed: 'Failed',
  unknown: 'Unknown',
}

export const DRIVE_HEALTH_COLORS: Record<DriveHealth, string> = {
  good: 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300',
  warning: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300',
  critical: 'bg-orange-100 text-orange-800 dark:bg-orange-900/50 dark:text-orange-300',
  failed: 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300',
  unknown: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
}

export interface StorageDrive {
  id: number
  asset_id: number
  name: string | null
  slot: string | null
  serial_number: string | null
  model: string | null
  drive_type: DriveType
  capacity_gb: number | null
  health: DriveHealth
  temperature_c: number | null
  wear_level_percent: number | null
  power_on_hours: number | null
  bad_sectors: number | null
  raid_array: string | null
  created_at: string
  updated_at: string
}

export interface StorageDriveCreate {
  name?: string | null
  slot?: string | null
  serial_number?: string | null
  model?: string | null
  drive_type?: DriveType
  capacity_gb?: number | null
  health?: DriveHealth
  temperature_c?: number | null
  wear_level_percent?: number | null
  power_on_hours?: number | null
  bad_sectors?: number | null
  raid_array?: string | null
}

export type StorageDriveUpdate = Partial<StorageDriveCreate>

// =============================================================================
// Disk Health Types (cross-asset drive overview)
// =============================================================================

export interface DiskHealthDrive {
  id: number
  asset_id: number
  asset_asset_id: string
  asset_name: string
  asset_type: AssetType
  infrastructure_source: InfrastructureSource | null

  name: string | null
  slot: string | null
  serial_number: string | null
  model: string | null
  drive_type: DriveType
  capacity_gb: number | null
  health: DriveHealth
  temperature_c: number | null
  wear_level_percent: number | null
  power_on_hours: number | null
  bad_sectors: number | null
  raid_array: string | null
  updated_at: string
}

export interface DiskHealthStats {
  total_drives: number
  total_capacity_tb: number
  by_health: Record<string, number>
  by_type: Record<string, number>
  hottest_drive_temp: number | null
  oldest_drive_hours: number | null
}

export type DiskHealthSortField =
  | 'device'
  | 'drive_type'
  | 'capacity_gb'
  | 'temperature_c'
  | 'power_on_hours'
  | 'wear_level_percent'
  | 'health'
  | 'updated_at'

export interface DiskHealthSearchParams {
  page?: number
  limit?: number
  search?: string
  health?: string
  drive_type?: string
  source?: string
  sort_by?: DiskHealthSortField
  sort_order?: SortOrder
}

// =============================================================================
// Storage Volume Types
// =============================================================================

export interface StorageVolume {
  id: number
  asset_id: number
  name: string | null
  filesystem: string | null
  total_gb: number | null
  used_gb: number | null
  usage_percent: number | null
  created_at: string
  updated_at: string
}

// =============================================================================
// Infrastructure Source Types
// =============================================================================

/**
 * Infrastructure sources for device lookup.
 * Used by AddByIPModal and infrastructure-specific pages.
 */
export type InfrastructureSource = 'proxmox' | 'unifi' | 'synology'

export const INFRASTRUCTURE_SOURCE_LABELS: Record<InfrastructureSource, string> = {
  proxmox: 'Proxmox',
  unifi: 'UniFi',
  synology: 'Synology',
}

/**
 * Console types for virtual console launcher.
 * Used to determine the correct URL pattern for management consoles.
 */
export type ConsoleType = 'idrac8' | 'idrac9' | 'supermicro' | 'ipmi' | 'custom'

export const CONSOLE_TYPE_LABELS: Record<ConsoleType, string> = {
  idrac8: 'Dell iDRAC 8',
  idrac9: 'Dell iDRAC 9',
  supermicro: 'Supermicro HTML5',
  ipmi: 'IPMI/BMC',
  custom: 'Custom URL',
}

// =============================================================================
// IP Lookup Types
// =============================================================================

export interface IPLookupAutoFill {
  name?: string | null
  asset_type?: AssetType | null
  model?: string | null // Server model (e.g., "PowerEdge R730") - from SSH dmidecode
  service_tag?: string | null // Service tag / serial number - from SSH dmidecode
  ip_address?: string | null
  mac_address?: string | null
  cpu_model?: string | null
  cpu_cores?: number | null
  cpu_threads?: number | null
  ram_gb?: number | null
  storage_capacity_gb?: number | null
  storage_count?: number | null
  storage_type?: string | null // "SSD", "HDD", "NVMe", "Mixed"
  network_ports?: number | null
  network_speed?: string | null // Network speed (e.g., "10GbE") - from SSH ethtool
  firmware_version?: string | null
  bios_version?: string | null // BIOS version - from SSH dmidecode
  dsm_update_available?: boolean | null // DSM update available (NAS only)
  dsm_update_info?: string | null // DSM update details JSON
  infrastructure_source?: InfrastructureSource | null
  firmware_update_available?: boolean | null
  firmware_update_version?: string | null
  status?: AssetStatus | null
  // Network info
  gateway?: string | null
  vlan_id?: number | null
  management_url?: string | null
  // Status info
  uptime_seconds?: number | null
  last_seen?: string | null // ISO format datetime string
  // Additional info
  notes?: string | null
  manufacturer_name?: string | null // Manufacturer name (e.g., "Dell Inc.") - from SSH
  // Multi-NIC and multi-drive support
  network_interfaces?: NetworkInterfaceCreate[]
  storage_drives?: StorageDriveCreate[]
  storage_volumes?: { name?: string; filesystem?: string; total_gb?: number; used_gb?: number; usage_percent?: number }[]
}

export interface IPLookupResponse {
  found: boolean
  source: 'proxmox' | 'unifi' | null
  device_type: string | null
  confidence: number
  auto_fill: IPLookupAutoFill | null
  message: string | null
  raw_data?: Record<string, unknown> | null
  // Credential prompt fields
  credentials_required?: boolean
  credentials_host?: string | null
  credentials_service_type?: 'proxmox' | 'pbs' | null
  credentials_port?: number | null
}

// =============================================================================
// Credential Management Types
// =============================================================================

export interface AddCredentialRequest {
  host: string
  port: number
  username: string
  password: string
  service_type: 'proxmox' | 'pbs'
}

export interface CredentialResponse {
  success: boolean
  host: string
  service_type: string
  message: string
  token_id?: string | null
}

// =============================================================================
// UniFi Query Gateway Types
// =============================================================================

export interface UniFiDeviceSummary {
  mac: string
  name: string
  model: string
  device_type: string // "switch", "access_point", "firewall"
  ip: string
  status: string // "online" / "offline"
  firmware: string
  upgrade_available: boolean
  port_count: number | null
  auto_fill: Record<string, unknown> // Full prefill data for AssetForm
  already_imported: boolean
  existing_asset_id: string | null
}

export interface QueryGatewayResponse {
  devices: UniFiDeviceSummary[]
  total_count: number
  new_count: number
  imported_count: number
}

// =============================================================================
// Motorcycle Types
// =============================================================================

export type BikeType =
  | 'cafe_racer'
  | 'cruiser'
  | 'custom'
  | 'dual_sport'
  | 'sport'
  | 'touring'
  | 'standard'
  | 'other'

export const BIKE_TYPE_LABELS: Record<BikeType, string> = {
  cafe_racer: 'Cafe Racer',
  cruiser: 'Cruiser',
  custom: 'Custom',
  dual_sport: 'Dual Sport',
  sport: 'Sport',
  touring: 'Touring',
  standard: 'Standard',
  other: 'Other',
}

export type BikeStatus = 'running' | 'restoration' | 'project' | 'storage' | 'sold'

export const BIKE_STATUS_LABELS: Record<BikeStatus, string> = {
  running: 'Running',
  restoration: 'Restoration',
  project: 'Project',
  storage: 'Storage',
  sold: 'Sold',
}

export const BIKE_STATUS_COLORS: Record<BikeStatus, string> = {
  running: 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300',
  restoration: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300',
  project: 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300',
  storage: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
  sold: 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300',
}

export interface MotorcycleImage {
  id: number
  image_url: string
  is_primary: boolean
  caption: string | null
  source: string | null
  category: string
  created_at: string
}

export interface Motorcycle {
  id: number
  motorcycle_id: string
  name: string
  make: string
  model: string
  year: number
  vin: string | null
  bike_type: BikeType
  status: BikeStatus
  engine_cc: number | null
  engine_type: string | null
  color: string | null
  purchase_date: string | null
  purchase_price: string | null
  image_url: string | null
  notes: string | null
  documentation_repo: string | null
  manufacturer_id: number | null
  location_id: number | null
  created_at: string
  updated_at: string
  images: MotorcycleImage[]
}

export interface MotorcycleSummary {
  id: number
  motorcycle_id: string
  name: string
  make: string
  model: string
  year: number
  status: BikeStatus
  image_url: string | null
}

export interface MotorcycleCreate {
  name: string
  make: string
  model: string
  year: number
  vin?: string | null
  bike_type?: BikeType
  status?: BikeStatus
  engine_cc?: number | null
  engine_type?: string | null
  color?: string | null
  purchase_date?: string | null
  purchase_price?: string | null
  image_url?: string | null
  notes?: string | null
  documentation_repo?: string | null
  manufacturer_id?: number | null
  location_id?: number | null
}

export type MotorcycleUpdate = Partial<MotorcycleCreate>

// =============================================================================
// Vendor Types
// =============================================================================

export interface Vendor {
  id: number
  name: string
  website: string | null
  country: string | null
  notes: string | null
  logo_url: string | null
  created_at: string
  updated_at: string
}

export interface VendorCreate {
  name: string
  website?: string | null
  country?: string | null
  notes?: string | null
  logo_url?: string | null
}

export type VendorUpdate = Partial<VendorCreate>

// =============================================================================
// Service Record Types
// =============================================================================

export interface ServiceRecord {
  id: number
  motorcycle_id: number
  service_date: string
  service_type: string
  mileage: number | null
  description: string
  cost: string | null
  vendor_id: number | null
  parts_used: string | null
  notes: string | null
  created_at: string
  updated_at: string
  vendor: Vendor | null
}

export interface ServiceRecordCreate {
  service_date: string
  service_type: string
  mileage?: number | null
  description: string
  cost?: string | null
  vendor_id?: number | null
  parts_used?: string | null
  notes?: string | null
}

export type ServiceRecordUpdate = Partial<ServiceRecordCreate>

// =============================================================================
// History Entry Types
// =============================================================================

export type HistoryEntryType = 'ownership' | 'accident' | 'modification' | 'milestone' | 'acquisition'

export interface HistoryEntry {
  id: number
  motorcycle_id: number
  entry_date: string
  title: string
  description: string
  entry_type: HistoryEntryType
  created_at: string
  updated_at: string
}

export interface HistoryEntryCreate {
  entry_date: string
  title: string
  description: string
  entry_type?: HistoryEntryType
}

export type HistoryEntryUpdate = Partial<HistoryEntryCreate>

// =============================================================================
// Ticket Types
// =============================================================================

export type TicketStatus = 'new' | 'in_progress' | 'resolved' | 'closed' | 'blocked' | 'waiting_external'
export type TicketPriority = 'critical' | 'high' | 'normal' | 'low'
export type TicketCategory = 'hardware' | 'software' | 'network' | 'personal' | 'other'
export type TicketSource = 'manual' | 'webhook' | 'email'
export type TicketSortField = 'created_at' | 'updated_at' | 'ticket_id' | 'title' | 'status' | 'priority'

export const TICKET_STATUS_LABELS: Record<TicketStatus, string> = {
  new: 'New',
  in_progress: 'In Progress',
  resolved: 'Resolved',
  closed: 'Closed',
  blocked: 'Blocked',
  waiting_external: 'Waiting',
}

export const TICKET_STATUS_COLORS: Record<TicketStatus, string> = {
  new: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  in_progress: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  resolved: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  closed: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
  blocked: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  waiting_external: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
}

export const TICKET_PRIORITY_LABELS: Record<TicketPriority, string> = {
  critical: 'Critical',
  high: 'High',
  normal: 'Normal',
  low: 'Low',
}

export const TICKET_PRIORITY_COLORS: Record<TicketPriority, string> = {
  critical: 'text-red-600 dark:text-red-400',
  high: 'text-orange-600 dark:text-orange-400',
  normal: 'text-blue-600 dark:text-blue-400',
  low: 'text-gray-500 dark:text-gray-400',
}

export const TICKET_PRIORITY_DOT_COLORS: Record<TicketPriority, string> = {
  critical: 'bg-red-500',
  high: 'bg-orange-500',
  normal: 'bg-blue-500',
  low: 'bg-gray-400',
}

export const TICKET_CATEGORY_LABELS: Record<TicketCategory, string> = {
  hardware: 'Hardware',
  software: 'Software',
  network: 'Network',
  personal: 'Personal',
  other: 'Other',
}

export const TICKET_SOURCE_LABELS: Record<TicketSource, string> = {
  manual: 'Manual',
  webhook: 'Webhook',
  email: 'Email',
}

export interface TicketAssetSummary {
  id: number
  asset_id: string
  name: string
  asset_type: string
  status: string
}

export interface TicketComment {
  id: number
  ticket_id: number
  content: string
  comment_type: 'comment' | 'status_change' | 'system'
  old_status: string | null
  new_status: string | null
  created_at: string
}

export interface TicketAttachment {
  id: number
  ticket_id: number
  filename: string
  file_url: string
  file_size: number
  mimetype: string
  created_at: string
}

export interface Ticket {
  id: number
  ticket_id: string
  title: string
  description: string | null
  status: TicketStatus
  priority: TicketPriority
  category: TicketCategory | null
  asset_id: number | null
  asset: TicketAssetSummary | null
  source: TicketSource
  source_ref: string | null
  comment_count: number
  attachment_count: number
  comments: TicketComment[]
  attachments: TicketAttachment[]
  created_at: string
  updated_at: string
  resolved_at: string | null
  closed_at: string | null
}

export interface TicketSummary {
  id: number
  ticket_id: string
  title: string
  status: TicketStatus
  priority: TicketPriority
  category: TicketCategory | null
  source: TicketSource
  asset_id: number | null
  asset_name: string | null
  comment_count: number
  attachment_count: number
  created_at: string
  updated_at: string
}

export interface TicketCreate {
  title: string
  description?: string | null
  priority?: TicketPriority
  category?: TicketCategory | null
  asset_id?: number | null
}

export interface TicketUpdate {
  title?: string
  description?: string | null
  status?: TicketStatus
  priority?: TicketPriority
  category?: TicketCategory | null
  asset_id?: number | null
  status_note?: string
}

export interface TicketCommentCreate {
  content: string
}

export interface TicketStats {
  total_count: number
  by_status: Record<string, number>
  by_priority: Record<string, number>
  by_category: Record<string, number>
}

export interface TicketSearchParams {
  page?: number
  limit?: number
  search?: string
  status?: TicketStatus | ''
  priority?: TicketPriority | ''
  category?: TicketCategory | ''
  asset_id?: number
  source?: TicketSource | ''
  sort_by?: TicketSortField
  sort_order?: SortOrder
}

// =============================================================================
// Timelog Types
// =============================================================================

export type WorkType = 'remote' | 'on_site'

export const WORK_TYPE_LABELS: Record<WorkType, string> = {
  remote: 'Fjarvinna',
  on_site: 'Á staðnum',
}

export interface TimelogCustomer {
  id: number
  name: string
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface TimelogCustomerCreate {
  name: string
}

export interface TimelogCustomerUpdate {
  name?: string
  is_active?: boolean
}

export interface TimelogEntry {
  id: number
  date: string
  customer_id: number
  customer_name: string
  work_type: WorkType
  description: string | null
  hours: number
  created_at: string
  updated_at: string
}

export interface TimelogEntryCreate {
  date: string
  customer_id: number
  work_type: WorkType
  description?: string | null
  hours: number
}

export interface TimelogEntryUpdate {
  date?: string
  customer_id?: number
  work_type?: WorkType
  description?: string | null
  hours?: number
}

export interface TimelogSearchParams {
  page?: number
  limit?: number
  year?: number
  month?: number
  customer_id?: number
}
